import { Ingreso, Egreso, Inversion, Patrimonio } from '../../../shared/types';

export class PatrimonioService {
  calculatePatrimonio(
    ingresos: Ingreso[], 
    egresos: Egreso[], 
    inversiones: Inversion[]
  ): Patrimonio {
    const totalIngresos = ingresos.reduce((sum, ingreso) => sum + ingreso.monto, 0);
    const totalEgresos = egresos.reduce((sum, egreso) => sum + egreso.monto, 0);
    const totalInversiones = inversiones.reduce((sum, inversion) => sum + inversion.monto, 0);
    
    const patrimonioNeto = totalIngresos - totalEgresos + totalInversiones;
    
    return {
      id: `patrimonio-${Date.now()}`,
      fecha: new Date(),
      totalIngresos,
      totalEgresos,
      totalInversiones,
      patrimonioNeto,
      createdAt: new Date(),
    };
  }

  calculatePatrimonioByMonth(
    year: number, 
    month: number, 
    ingresos: Ingreso[], 
    egresos: Egreso[], 
    inversiones: Inversion[]
  ): Patrimonio {
    const monthIngresos = ingresos.filter(ingreso => {
      const fecha = ingreso.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
    
    const monthEgresos = egresos.filter(egreso => {
      const fecha = egreso.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
    
    const monthInversiones = inversiones.filter(inversion => {
      const fecha = inversion.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });

    const totalIngresos = monthIngresos.reduce((sum, ingreso) => sum + ingreso.monto, 0);
    const totalEgresos = monthEgresos.reduce((sum, egreso) => sum + egreso.monto, 0);
    const totalInversiones = monthInversiones.reduce((sum, inversion) => sum + inversion.monto, 0);
    
    const patrimonioNeto = totalIngresos - totalEgresos + totalInversiones;
    
    return {
      id: `patrimonio-${year}-${month}`,
      fecha: new Date(year, month, 1),
      totalIngresos,
      totalEgresos,
      totalInversiones,
      patrimonioNeto,
      createdAt: new Date(),
    };
  }

  calculatePatrimonioByYear(
    year: number, 
    ingresos: Ingreso[], 
    egresos: Egreso[], 
    inversiones: Inversion[]
  ): Patrimonio {
    const yearIngresos = ingresos.filter(ingreso => ingreso.fecha.getFullYear() === year);
    const yearEgresos = egresos.filter(egreso => egreso.fecha.getFullYear() === year);
    const yearInversiones = inversiones.filter(inversion => inversion.fecha.getFullYear() === year);

    const totalIngresos = yearIngresos.reduce((sum, ingreso) => sum + ingreso.monto, 0);
    const totalEgresos = yearEgresos.reduce((sum, egreso) => sum + egreso.monto, 0);
    const totalInversiones = yearInversiones.reduce((sum, inversion) => sum + inversion.monto, 0);
    
    const patrimonioNeto = totalIngresos - totalEgresos + totalInversiones;
    
    return {
      id: `patrimonio-${year}`,
      fecha: new Date(year, 0, 1),
      totalIngresos,
      totalEgresos,
      totalInversiones,
      patrimonioNeto,
      createdAt: new Date(),
    };
  }

  getPatrimonioHistory(
    ingresos: Ingreso[], 
    egresos: Egreso[], 
    inversiones: Inversion[],
    months: number = 12
  ): Patrimonio[] {
    const history: Patrimonio[] = [];
    const currentDate = new Date();
    
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      
      const patrimonio = this.calculatePatrimonioByMonth(year, month, ingresos, egresos, inversiones);
      history.push(patrimonio);
    }
    
    return history;
  }

  getBalanceByMonth(
    year: number, 
    month: number, 
    ingresos: Ingreso[], 
    egresos: Egreso[]
  ): { ingresos: number; egresos: number; balance: number } {
    const monthIngresos = ingresos.filter(ingreso => {
      const fecha = ingreso.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
    
    const monthEgresos = egresos.filter(egreso => {
      const fecha = egreso.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });

    const totalIngresos = monthIngresos.reduce((sum, ingreso) => sum + ingreso.monto, 0);
    const totalEgresos = monthEgresos.reduce((sum, egreso) => sum + egreso.monto, 0);
    const balance = totalIngresos - totalEgresos;
    
    return {
      ingresos: totalIngresos,
      egresos: totalEgresos,
      balance
    };
  }

  getCategoryBreakdown(
    egresos: Egreso[], 
    year: number, 
    month: number
  ): { [categoria: string]: number } {
    const monthEgresos = egresos.filter(egreso => {
      const fecha = egreso.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });

    const breakdown: { [categoria: string]: number } = {};
    monthEgresos.forEach(egreso => {
      breakdown[egreso.categoria] = (breakdown[egreso.categoria] || 0) + egreso.monto;
    });

    return breakdown;
  }
}
